"use client"

import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { ArrowDownLeftIcon, ArrowUpRightIcon, EyeOffIcon } from "lucide-react"
import type { FinanceHomeOverview } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { MirroredWeeklyChart } from "@/features/dashboard/home-d4/components/mirrored-weekly-chart"
import { useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"

const isZero = (value: string) => Number(value) === 0
const isNegative = (value: string) => value.trim().startsWith("-")

/**
 * Treasury (D4 hero): what the branch holds today, what came in and went out over the period, and a mirrored
 * weekly chart. When branch autonomy withholds the figures the tile says so plainly: it never shows a share in
 * a money format, which is what the old home did with `figuresAreRatios`.
 */
export function TreasuryTile({
  overview,
  financeLink,
  recordOffering,
}: {
  overview: FinanceHomeOverview
  financeLink: HomeTileLink
  /** "Record an offering" when the reader may record income; null otherwise. */
  recordOffering: HomeTileLink | null
}) {
  const t = useTranslations("dashboard.home.treasury")
  const { figures, currency } = overview

  if (!overview.disclosed || !figures) {
    return (
      <HomeTile title={t("title")} link={financeLink} testId='home-tile-treasury'>
        <div
          data-testid='treasury-withheld'
          className='flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-border/60 bg-muted/30 px-4 py-6 text-center'
        >
          <span className='inline-flex size-8 items-center justify-center rounded-full bg-background text-muted-foreground ring-1 ring-border/60'>
            <EyeOffIcon aria-hidden className='size-4' />
          </span>
          <p className='text-sm font-semibold text-foreground'>{t("withheld.title")}</p>
          <p className='max-w-md text-xs text-muted-foreground'>{t("withheld.description")}</p>
        </div>
      </HomeTile>
    )
  }

  const noIncomeYet = isZero(figures.income)
  const chartEmpty = figures.weekly.every((week) => isZero(week.income) && isZero(week.expense))
  const netNegative = isNegative(figures.netChange)

  return (
    <HomeTile title={t("title")} link={financeLink} testId='home-tile-treasury'>
      <div className='flex min-h-0 flex-1 flex-col gap-6 md:flex-row md:gap-7'>
        <div className='flex min-w-[15.5rem] flex-col gap-1.5'>
          <span className='text-[13px] font-bold text-muted-foreground'>{t("cashToday")}</span>
          <span
            data-testid='treasury-cash'
            className='text-[2.5rem] leading-none font-extrabold tracking-tight whitespace-nowrap text-foreground tabular-nums'
          >
            {formatCurrency(figures.cashOnHand, currency)}
          </span>
          <span className='mt-1 flex'>
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums",
                netNegative
                  ? "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"
                  : "bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
              )}
            >
              {t("netChange", {
                amount: `${netNegative ? "" : "+"}${formatCurrency(figures.netChange, currency)}`,
              })}
            </span>
          </span>
          <dl className='mt-3.5 flex flex-col gap-2 text-[13.5px]'>
            <FigureRow
              icon={<ArrowDownLeftIcon aria-hidden className='size-4 text-teal-600 dark:text-teal-400' />}
              label={t("income")}
              value={formatCurrency(figures.income, currency)}
            />
            <FigureRow
              icon={<ArrowUpRightIcon aria-hidden className='size-4 text-rose-600 dark:text-rose-400' />}
              label={t("expenses")}
              value={formatCurrency(figures.expenses, currency)}
            />
            <FigureRow
              icon={<ArrowUpRightIcon aria-hidden className='size-4 text-muted-foreground/70' />}
              label={t("remittances")}
              value={formatCurrency(figures.remittancesToParish, currency)}
            />
          </dl>
          {noIncomeYet && !chartEmpty ? (
            <p data-testid='treasury-no-income' className='mt-2 text-xs text-muted-foreground'>
              {t("noIncomeYet")}{" "}
              {recordOffering ? (
                <Link to={recordOffering.href} className='font-bold text-primary hover:underline'>
                  {recordOffering.label}
                </Link>
              ) : null}
            </p>
          ) : null}
        </div>

        <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-1.5'>
          <div className='flex items-center gap-3'>
            <span className='text-[12.5px] font-bold text-muted-foreground'>
              {t("lastWeeks", { count: figures.weekly.length })}
            </span>
            <span className='ml-auto flex gap-3.5 text-[12.5px] text-muted-foreground'>
              <span className='flex items-center gap-1.5'>
                <span aria-hidden className='size-2.5 rounded-[3px] bg-teal-600 dark:bg-teal-400' />
                {t("legendIncome")}
              </span>
              <span className='flex items-center gap-1.5'>
                <span aria-hidden className='size-2.5 rounded-[3px] bg-rose-600 dark:bg-rose-400' />
                {t("legendExpense")}
              </span>
            </span>
          </div>
          {chartEmpty ? (
            <HomeTileEmpty message={t("noIncomeYet")} action={recordOffering} />
          ) : (
            <MirroredWeeklyChart weeks={figures.weekly} currency={currency} />
          )}
        </div>
      </div>
    </HomeTile>
  )
}

function FigureRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className='flex items-center justify-between gap-4'>
      <dt className='flex items-center gap-2 text-muted-foreground'>
        {icon}
        {label}
      </dt>
      <dd className='font-bold tabular-nums text-foreground'>{value}</dd>
    </div>
  )
}
